import { Badge, Card } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PriceWithSale from "./PriceWithSale";
import { getProductDisplayName } from "../utils/productTitle";
import { formatMaterial } from "../utils/material";
import { getDisplayCoating } from "../utils/coating";
import { isBrakePadProduct, isBrakeRotorProduct } from "../utils/brakeParts";
import { buildProductUrl, getCurrentRoute } from "../utils/navigation";
import { getProductCategoryLabel } from "../utils/partCategoryOptions";

const Product = ({ product }) => {
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const isComingSoon = product.status === 'comingSoon';
  const isInStock = !isComingSoon && Number(product.stockQty) > 0;
  const isTieredUser = Boolean(userInfo?.tier?.id);
  const tierPrice =
    isTieredUser && product?.tierPrice != null
      ? Number(product.tierPrice)
      : null;

  // Preserve current list URL (filters/pagination) for BackButton via location.state.from.
  // Carry an exact vehicle selected in the listing flow to the product page so
  // related products never have to infer a vehicle from a large fitment list.
  const from = getCurrentRoute(location);
  const searchParams = new URLSearchParams(location.search || "");
  const inheritedVehicle = location.state?.vehicleContext;
  const make = String(inheritedVehicle?.make || searchParams.get("make") || "").trim();
  const model = String(inheritedVehicle?.model || searchParams.get("model") || "").trim();
  const year = String(inheritedVehicle?.year || searchParams.get("year") || "").trim();
  const trim = String(inheritedVehicle?.trim || searchParams.get("trim") || "").trim();
  const vehicleContext = make && model && year
    ? { make, model, year, ...(trim ? { trim } : {}) }
    : undefined;

  const displayName = getProductDisplayName(product);
  const displayCoating = getDisplayCoating(product);

  const effectiveBrakeSpecType = product?.brakeSpecType === "pad" || product?.brakeSpecType === "rotor" || product?.brakeSpecType === "none"
    ? product.brakeSpecType
    : isBrakePadProduct(product) ? "pad" : isBrakeRotorProduct(product) ? "rotor" : "none";
  const rotorBadgeText = effectiveBrakeSpecType === "rotor" && product?.brakeRotorSpecs?.rotorSizeLabel
    ? `Rotor: ${product.brakeRotorSpecs.rotorSizeLabel}`
    : effectiveBrakeSpecType === "rotor" && ((Number(product?.brakeRotorSpecs?.outerDiameterMm) > 0) || (Number(product?.brakeRotorSpecs?.outerDiameterIn) > 0))
      ? `Rotor: ${product?.brakeRotorSpecs?.outerDiameterMm ? `${product.brakeRotorSpecs.outerDiameterMm} mm` : ''}${product?.brakeRotorSpecs?.outerDiameterMm && product?.brakeRotorSpecs?.outerDiameterIn ? ' / ' : ''}${product?.brakeRotorSpecs?.outerDiameterIn ? `${product.brakeRotorSpecs.outerDiameterIn} in` : ''}`
      : '';
  const padSizeBadgeText = effectiveBrakeSpecType === "pad" && product?.brakePadSpecs?.manufacturerPadSizeText
    ? `Pad Size: ${product.brakePadSpecs.manufacturerPadSizeText}`
    : '';


  return (
    <Card className="my-3 p-3 rounded product-card">
      <Link
        to={buildProductUrl(product._id, location)}
        state={{ from, ...(vehicleContext ? { vehicleContext } : {}) }}
        className="product-card-link"
        aria-label={`View product: ${displayName}`}
      >
        <Card.Img src={product.image} loading="lazy" variant="top" alt={displayName} />

        <Card.Body>
          {/* Hover info box */}
          <div
            className={`product-hover-extra ${
              isInStock ? "product-hover-extra--in" : "product-hover-extra--out"
            }`}
          >
            {isComingSoon ? (
              <>
                <p className="ph-status">Coming Soon</p>
                <p className="ph-delivery">Arriving soon</p>
                <p className="ph-pickup">Not available for purchase yet</p>
                <p className="ph-return">Check back for availability</p>
              </>
            ) : isInStock ? (
              <>
                <p className="ph-status">In stock</p>
                <p className="ph-delivery">Shipping available</p>
                <p className="ph-pickup">In-store pickup available</p>
                <p className="ph-return">30-day returns</p>
              </>
            ) : (
              <>
                <p className="ph-status ph-status-out">Out of stock</p>
                <p className="ph-delivery">Currently unavailable for shipping</p>
                <p className="ph-pickup">Currently unavailable for pickup</p>
                <p className="ph-return">Contact us for availability</p>
              </>
            )}
          </div>

          {isComingSoon && (
            <Badge bg="warning" text="dark" className="mb-2">Coming Soon</Badge>
          )}
          <Card.Title as="div" className="product-title">
            <strong>{displayName}</strong>
          </Card.Title>

          {(product.category || product.position || product.material || displayCoating || product.partNumber) && (
            <div className="product-meta-badges mb-2 text-muted d-flex flex-wrap gap-2">
              {product.category && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {getProductCategoryLabel(product.category)}
                </span>
              )}
              {product.position && product.position !== "none" && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {product.position}
                </span>
              )}
              {product.material && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {formatMaterial(product.material)}
                </span>
              )}
              {displayCoating && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {displayCoating}
                </span>
              )}
              {rotorBadgeText && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {rotorBadgeText}
                </span>
              )}
              {padSizeBadgeText && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  {padSizeBadgeText}
                </span>
              )}
              {product.partNumber && (
                <span className="product-meta-badge badge bg-light text-dark border">
                  OEM: {product.partNumber}
                </span>
              )}
            </div>
          )}

          <Card.Text as="h3">
            <PriceWithSale
              price={Number(product.price)}
              salePrice={product.salePrice != null ? Number(product.salePrice) : null}
              saleStartAt={product.saleStartAt}
              saleEndAt={product.saleEndAt}
              tierPrice={tierPrice}
              showTierTag={Boolean(tierPrice)}
              forceBasePrice={isTieredUser && !tierPrice}
            />
          </Card.Text>
        </Card.Body>
      </Link>
    </Card>
  );
};

export default Product;
